import React, { Fragment, useMemo, useState } from 'react'

import { HeaderResultOption, HeaderResultOptionsButton } from './HeaderResultOptions/HeaderResultOptionsButton'
import { HeaderResultCommentsButton } from './HeaderResultCommentsButton/HeaderResultCommentsButton'
import { HeaderResultCopyAllButton } from './HeaderResultCopyAllButton/HeaderResultCopyAllButton'
import {
  HeaderResultOptionConfig,
  useCopyLinkHeaderResultOption,
  useEditHeaderResultOption,
  useFeedbackHeaderResultOption,
  useRerunHeaderResultOption,
  useSalesforceHeaderResultOption,
  useWealthboxHeaderResultOption,
} from './HeaderResultOptions/DefaultHeaderResultOptions'
import { ResultCardHeaderButtonsProps } from './ResultCardHeaderButtons'
import { ResultCardHeaderCRMButtons } from './ResultCardHeaderCRMButtons'
import { ResultCardHeaderEmailButtons } from './ResultCardHeaderEmailButtons'

import {
  getIntegrationServiceIdsForCategory,
  getMailIntegrationServiceLabel,
  IntegrationCategory,
  IntegrationServiceId,
} from '../../../../../../../common/integrations'
import { selectRoomSession } from '../../../../../../../store/meetingSummary/roomSession'
import { selectCurrentUser } from '../../../../../../../store/meetingSummary/user'
import { AiFeature } from '../../../../../../../models/AiFeature'
import { AiResult } from '../../../../../../../models/AiResult'
import { useAppSelector } from '../../../../../../../store/hooks'

import { getAIFeatureAndResultTrackingDetails } from '../../../../tracking'
import { TrackEvent, useTracking } from '../../../../common/tracking'

import { copyResult, TEXT_TYPE } from '../../../copy'
import { useCopyData } from '../../../hooks'
import { ExternalUserServiceIntegration } from '../../../../../../../models/User'
import { AddResultToEmailProviderModal } from '../../../../../../../views/MeetingSummary/EmailProvider/AddResultToEmailProviderModal'
import { useEffectiveConfig } from '../../../../../../../common/useEffectiveConfig'
import { decorateEmailWithCompliance } from '../../../../../../../common/useMailtoDecorateWithCompliance'

export const useMailtoHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption => {
  const { tracked } = useTracking()
  const roomSession = useAppSelector(selectRoomSession)
  const copyData = useCopyData()
  const currentUser = useAppSelector(selectCurrentUser)
  const { emailComplianceMessage } = useEffectiveConfig()

  const _handleMailtoClicked = async () => {
    if (!roomSession || !currentUser) {
      return
    }
    const copiedResult = copyResult(aiFeature, aiResult, copyData)

    let emailBody = copiedResult[TEXT_TYPE]
    if (emailComplianceMessage) {
      emailBody = decorateEmailWithCompliance(copiedResult[TEXT_TYPE], emailComplianceMessage)
    }
    const { users } = roomSession
    const clientEmails = users
      .filter((user) => user.roles.some((role) => role.name === 'Client') && user.email)
      .map((user) => user.email)
      .join(',')
    const ccEmails = users
      .filter((user) => user.roles.every((role) => role.name !== 'Client') && user.id !== currentUser.id && user.email)
      .map((user) => user.email)
      .join(',')
    window.location.href = `mailto:${clientEmails ? encodeURIComponent(clientEmails) : ''}?${
      ccEmails ? 'cc=' + encodeURIComponent(ccEmails) + '&' : ''
    }body=${encodeURIComponent(emailBody)}`
  }

  const handleMailtoClicked = tracked(_handleMailtoClicked, {
    eventName: TrackEvent.AI_RESULT_EMAIL_CLICKED,
    eventDetails: {
      ...getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
    },
  })

  return {
    selectOption: {
      id: 'mailto',
      label: 'Send email',
    },
    icon: 'bi-send',
    handleSelected: handleMailtoClicked,
  }
}

const getRelatedMailIntegration = (integrations?: ExternalUserServiceIntegration[], serviceId?: string): ExternalUserServiceIntegration | null => {
  if (serviceId === IntegrationServiceId.OUTLOOK_CALENDAR) {
    return integrations?.find((e) => e.serviceId === IntegrationServiceId.OUTLOOK_MAIL) ?? null
  }
  if (serviceId === IntegrationServiceId.GOOGLE_CALENDAR) {
    return integrations?.find((e) => e.serviceId === IntegrationServiceId.GMAIL) ?? null
  }
  return integrations?.find((e) => getIntegrationServiceIdsForCategory(IntegrationCategory.MAIL).includes(e.serviceId as IntegrationServiceId)) ?? null
}

const useSaveToEmailProviderHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult) => {
  const currentUser = useAppSelector(selectCurrentUser)
  const { integrations: currentUserIntegrations } = currentUser ?? {}

  const [showModal, setShowModal] = useState<boolean>(false)

  const calendarIntegration = currentUserIntegrations?.find((e) =>
    getIntegrationServiceIdsForCategory(IntegrationCategory.CALENDAR).includes(e.serviceId as IntegrationServiceId),
  )
  const { serviceId } = getRelatedMailIntegration(currentUserIntegrations, calendarIntegration?.serviceId) ?? {}

  const handleSaveToEmailProviderClicked = () => serviceId && setShowModal(true)

  return {
    selectOption: {
      id: 'send-to-email-provider',
      label: `Send to ${serviceId ? getMailIntegrationServiceLabel(serviceId) : 'email provider'}`,
      disabled: !serviceId,
    },
    icon: 'bi-send',
    handleSelected: handleSaveToEmailProviderClicked,
    children:
      serviceId && showModal ? (
        <AddResultToEmailProviderModal aiFeature={aiFeature} aiResult={aiResult} show={showModal} onHide={() => setShowModal(false)} serviceId={serviceId} />
      ) : null,
    triggerOverlay: !serviceId,
    tooltipLabel: 'Connect your email provider to Zocks to enable',
  }
}

export const EmailResultCardHeaderButtons: React.FC<ResultCardHeaderButtonsProps> = ({
  className,
  aiFeature,
  aiResult,
  collapsed,
  setCollapsed,
  noResults,
  extraHeaderOptions,
  headerResultOptionsConfig: additionalHeaderResultOptionsConfig,
}) => {
  const headerResultOptionsConfig: HeaderResultOptionConfig = useMemo(
    () => ({
      resultIsCollapsed: collapsed,
      setResultCollapsed: setCollapsed,
      hiddenOptions: [],
      ...additionalHeaderResultOptionsConfig,
    }),
    [collapsed, setCollapsed, additionalHeaderResultOptionsConfig],
  )

  const headerResultOptions = [
    useRerunHeaderResultOption(aiFeature, aiResult),
    useEditHeaderResultOption(aiFeature, aiResult, headerResultOptionsConfig),
    useMailtoHeaderResultOption(aiFeature, aiResult),
    useSaveToEmailProviderHeaderResultOption(aiFeature, aiResult),
    useCopyLinkHeaderResultOption(aiResult),
    useFeedbackHeaderResultOption(aiFeature, aiResult),
    useWealthboxHeaderResultOption(aiFeature, aiResult),
    useSalesforceHeaderResultOption(aiFeature, aiResult),
  ].filter((resultOption): resultOption is HeaderResultOption => !!resultOption)
  const extraChildren = (
    <>
      {headerResultOptions.map(({ children }, idx) => (
        <Fragment key={idx}>{children}</Fragment>
      ))}
      {extraHeaderOptions ? extraHeaderOptions.map(({ children }, idx) => <Fragment key={idx}>{children}</Fragment>) : null}
    </>
  )

  return (
    <div className={`d-flex py-2 align-items-center gap-2 ${className ?? ''}`}>
      <HeaderResultCommentsButton aiFeature={aiFeature} aiResult={aiResult} />
      <HeaderResultCopyAllButton aiFeature={aiFeature} aiResult={aiResult} />
      <ResultCardHeaderCRMButtons aiFeature={aiFeature} aiResult={aiResult} />
      <ResultCardHeaderEmailButtons aiFeature={aiFeature} aiResult={aiResult} />
      <HeaderResultOptionsButton disabled={noResults} resultOptions={[...headerResultOptions, ...(extraHeaderOptions || [])]} />
      {extraChildren}
    </div>
  )
}
